import { type TFunction } from 'i18next';
import { z } from 'zod';
import { CLIENT_ACRONYM_MAX_LENGTH, CreateClientSchema } from '@ooh/contracts';

/**
 * Client form schema — used for both create and edit dialogs.
 *
 * `name` is required (min 1) so an empty name is blocked client-side.
 * Optional fields default to undefined, matching what the server expects.
 *
 * `acronym` keeps `.min(1)` (PR #1128 review, finding 5): an explicitly
 * emptied field — clearing a touched value in create mode, or clearing it
 * at all in edit mode, which always submits the field — must fail
 * client-side with an inline error rather than reach the server and surface
 * a generic toast. The untouched create-mode preview never actually needs
 * the empty string to validate: it is always derived from the name field
 * via `deriveAcronymPreview`, and an empty name already fails its own
 * `.min(1)`, so the form is invalid for that reason regardless. Length and
 * separator rules are kept so an over-long or `/`-containing value the
 * caller actually typed is still blocked client-side.
 *
 * Pass `t` from the `clients` namespace so Zod produces a translated
 * error message that surfaces in the `name`/`acronym` field helper text.
 */
export function createClientFormSchema(t: TFunction<'clients'>) {
  return CreateClientSchema.extend({
    brands: z.array(z.string().trim().min(1, t('dialog.fields.brandRequired'))).default([]),
    name: z.string().min(1, t('dialog.fields.nameRequired')),
    acronym: z
      .string()
      .min(1, t('dialog.fields.acronymRequired'))
      .max(CLIENT_ACRONYM_MAX_LENGTH, t('dialog.fields.acronymTooLong'))
      .refine((value) => !value.includes('/'), { message: t('dialog.fields.acronymInvalidChar') })
      .optional(),
  });
}

export type ClientFormValues = z.infer<ReturnType<typeof createClientFormSchema>>;
